import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { CartController } from './cart.js';

const RESET_DELAY = 2500;

/**
 * @typedef {object} ShopRitualAddAllRefs
 * @property {HTMLButtonElement} button - The "add all to bag" button.
 * @property {HTMLElement} label - The button label.
 * @property {HTMLElement} [liveRegion] - The live region announcing the result.
 */

/**
 * Adds every product of the ritual to the cart in a single cart/add request.
 *
 * @extends {Component<ShopRitualAddAllRefs>}
 */
class ShopRitualAddAll extends Component {
  requiredRefs = ['button', 'label'];

  /** @type {boolean} */
  #pending = false;

  /** @type {string | undefined} */
  #initialLabel;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timeout;

  /** @returns {{ id: number, quantity: number }[]} */
  get items() {
    return (this.dataset.variantIds ?? '')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => Number.isFinite(id) && id > 0)
      .map((id) => ({ id, quantity: 1 }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timeout) clearTimeout(this.#timeout);
  }

  /**
   * Adds all of the section's products to the cart.
   * @param {Event} [event] - The click event.
   */
  async addAll(event) {
    event?.preventDefault();

    const items = this.items;
    if (this.#pending || items.length === 0) return;

    const { button, label, liveRegion } = this.refs;
    const initialLabel = this.#initialLabel ?? label.textContent ?? '';
    this.#initialLabel = initialLabel;

    this.#pending = true;
    if (this.#timeout) clearTimeout(this.#timeout);
    button.disabled = true;
    this.dataset.state = 'loading';

    try {
      const cart = await CartController.add(items);

      // A cart error response carries a `status` — the theme's cart controller resolves it as data.
      if (cart?.status) throw new Error(cart.description || cart.message || 'Cart error');

      this.dataset.state = 'added';
      label.textContent = this.dataset.addedText || initialLabel;
      if (liveRegion) liveRegion.textContent = label.textContent;

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'shop-ritual-add-all', {
          source: 'shop-ritual-add-all',
          itemCount: items.length,
        })
      );
    } catch (error) {
      console.error('Shop ritual: unable to add all items to the cart', error);
      this.dataset.state = 'error';
    } finally {
      this.#pending = false;
      button.disabled = false;

      this.#timeout = setTimeout(() => {
        this.dataset.state = 'idle';
        label.textContent = initialLabel;
        if (liveRegion) liveRegion.textContent = '';
      }, RESET_DELAY);
    }
  }
}

if (!customElements.get('shop-ritual-add-all')) {
  customElements.define('shop-ritual-add-all', ShopRitualAddAll);
}
